'use client';

import Script from 'next/script';
import { initFirebaseAnalytics as initializeAnalyticsService } from '@/lib/firebaseAnalytics';

export default function GoogleAnalyticsScripts() {
    const measurementId = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;

    if (!measurementId) {
        return null;
    }

    const initAndLogPageView = async () => {
        try {
            const analyticsInstance = await initializeAnalyticsService();
            if (analyticsInstance) {
                const { logEvent } = await import('firebase/analytics');
                logEvent(analyticsInstance, 'page_view', {
                    page_path: window.location.pathname,
                    page_title: document.title,
                });
                console.log(
                    '[GoogleAnalyticsScripts] Firebase Analytics initialized and page_view logged via initAndLogPageView.'
                );
            } else {
                console.warn(
                    '[GoogleAnalyticsScripts] Firebase Analytics instance is null after init. Page view not logged by this component.'
                );
            }
        } catch (error) {
            console.error(
                '[GoogleAnalyticsScripts] Error during Firebase Analytics init or page_view logging:',
                error
            );
        }
    };

    return (
        <>
            <Script
                strategy="afterInteractive"
                src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
                onError={(e) => {
                    console.error(
                        '[GoogleAnalyticsScripts] Error loading gtag.js script.',
                        e
                    );
                }}
            />
            <Script
                id="google-analytics-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        // Restore the gtag config call now that the duplicate request issue is fixed
                        gtag('config', '${measurementId}', {
                            page_path: window.location.pathname, // gtag config handles initial page_view
                        });
                    `,
                }}
                onLoad={async () => {
                    console.log(
                        '[GoogleAnalyticsScripts] Google Analytics inline script (gtag config part) processed.'
                    );
                    // Call the new async function that handles dynamic import for logEvent if needed for additional tracking
                    // However, gtag('config', ...) already sends a page_view.
                    // If initFirebaseAnalytics was meant for custom events or user properties, it can be called here.
                    // For this task, focusing on `logEvent` if it were used for custom events.
                    // Since gtag config sends page_view, additional logEvent for page_view might be redundant unless specifically for Firebase console.
                    await initializeAnalyticsService(); // Ensure analytics service is initialized for other potential events.
                }}
                onError={(e) => {
                    console.error(
                        '[GoogleAnalyticsScripts] Error in Google Analytics inline script part or its onLoad handler.',
                        e
                    );
                }}
            />
        </>
    );
}
